import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import LoadingSpinner from '../components/LoadingSpinner'
import { getPostAuthPath, withAuthQuery } from '../lib/authRedirect'

function AuthCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { handleOAuthCallback } = useAuth()
  const [error, setError] = useState(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const handleCallback = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession()

        if (sessionError) {
          console.error('OAuth callback error:', sessionError)
          setError(sessionError.message)
          setTimeout(() => navigate(withAuthQuery('/login', searchParams)), 3000)
          return
        }

        if (data?.session) {
          const result = await handleOAuthCallback(data.session)
          if (result?.success) {
            navigate(getPostAuthPath(searchParams), { replace: true })
            return
          }
          setError(result?.error || 'Authentication failed')
          setTimeout(() => navigate(withAuthQuery('/login', searchParams)), 3000)
          return
        }

        setError('No session found')
        setTimeout(() => navigate(withAuthQuery('/login', searchParams)), 3000)
      } catch (err) {
        console.error('Unexpected error during OAuth callback:', err)
        setError('Authentication failed. Redirecting to login...')
        setTimeout(() => navigate(withAuthQuery('/login', searchParams)), 3000)
      }
    }

    handleCallback()
  }, [navigate, handleOAuthCallback, searchParams])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full text-center space-y-6">
        {error ? (
          <div>
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              <p className="font-semibold mb-2">Authentication Error</p>
              <p className="text-sm">{error}</p>
            </div>
            <p className="text-gray-600">Redirecting to login page...</p>
          </div>
        ) : (
          <div>
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Completing sign in...
            </h2>
            <p className="text-gray-600">
              Please wait while we authenticate your account.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default AuthCallbackPage
